import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { X } from "lucide-react"

export interface QuickFilterOption {
  value: string
  label: string
  count?: number
}

export interface QuickFilterGroup {
  key: string
  label: string
  options: QuickFilterOption[]
}

interface QuickFilterBarProps {
  groups: QuickFilterGroup[]
  values: Record<string, string | null>
  onChange: (key: string, value: string | null) => void
  className?: string
}

/**
 * Barra de filtros rápidos com toggle chips
 */
export function QuickFilterBar({ groups, values, onChange, className }: QuickFilterBarProps) {
  const hasActiveFilters = Object.values(values).some(v => v !== null)

  const handleClearAll = () => {
    groups.forEach(group => {
      onChange(group.key, null)
    })
  }

  return (
    <div className={cn("flex flex-wrap items-center gap-3", className)}>
      {groups.map(group => (
        <div key={group.key} className="flex items-center gap-1.5">
          <span className="text-muted-foreground text-sm font-medium">{group.label}:</span>
          <div className="flex gap-1">
            {group.options.map(option => {
              const isActive = values[group.key] === option.value

              return (
                <Button
                  key={option.value}
                  variant={isActive ? "default" : "outline"}
                  size="sm"
                  className={cn("h-7 gap-1.5 px-2.5 text-xs", isActive && "ring-2 ring-offset-1")}
                  onClick={() => onChange(group.key, isActive ? null : option.value)}
                >
                  {option.label}
                  {option.count !== undefined && (
                    <Badge
                      variant={isActive ? "secondary" : "outline"}
                      className="h-4 px-1 text-[10px]"
                    >
                      {option.count}
                    </Badge>
                  )}
                </Button>
              )
            })}
          </div>
        </div>
      ))}

      {hasActiveFilters && (
        <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs" onClick={handleClearAll}>
          <X className="h-3 w-3" />
          Limpar filtros
        </Button>
      )}
    </div>
  )
}
